import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { ArrowUpRight, Banknote, CalendarClock, CreditCard, Info, KeyRound, Landmark, Loader2, Lock, Play, QrCode, ReceiptText, Sparkles, WalletCards, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useOrganization } from "@/contexts/OrganizationContext";
import { OrganizationRole, OrgPermission } from "@tickethall/core";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable, DataTableColumn } from "@/components/DataTable";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { createPortal } from "react-dom";
import { useEventFocusSlot } from "@/contexts/EventFocusSlotContext";
import { ProducerFinancialEventSidebar } from "@/components/producer/ProducerFinancialEventSidebar";
import { WithdrawalRulesModal } from "@/components/producer/WithdrawalRulesModal";
import { ProducerWithdrawalGantt } from "@/components/producer/ProducerWithdrawalGantt";
import { WithdrawalEventPicker } from "@/components/producer/WithdrawalEventPicker";
import { WithdrawalRequestModal } from "@/components/producer/WithdrawalRequestModal";
import { RequestRoleModal } from "@/components/producer/RequestRoleModal";
import { EventImage } from "@/components/EventImage";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { cn, formatBRL } from "@/lib/utils";
import { getProducerDashboardStats } from "@/lib/api-producer";
import { panelAccentDot, panelControl, panelEmpty, panelEyebrow, panelFieldLabel, panelPill, panelRow, panelSectionHeader, panelSectionTitle, panelSurface } from "@/lib/panel-ui";

function addBusinessDays(from: Date, days: number) {
  const date = new Date(from);
  let remaining = days;
  while (remaining > 0) {
    date.setDate(date.getDate() + 1);
    if (date.getDay() !== 0 && date.getDay() !== 6) remaining--;
  }
  return date;
}

function parseWithdrawalAmount(value: string) {
  const normalized = value.replace(/\./g, "").replace(",", ".").replace(/[^\d.-]/g, "");
  return Number(normalized) || 0;
}

function formatWithdrawalAmount(value: number) {
  return value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

interface BalanceRow {
  event_id: string;
  event_title: string;
  event_status: string;
  event_end: string | null;
  cover_image_url?: string | null;
  start_date?: string | null;
  gross_total: number;
  refunds_total: number;
  platform_fee_total: number;
  gateway_fee_total: number;
  pix_total: number;
  pix_released: number;
  pix_pending_release: number;
  pix_next_release: string | null;
  credit_card_total: number;
  boleto_total: number;
  post_event_released: boolean;
  post_event_release: string | null;
  released_total: number;
  pending_withdrawal: number;
  total_withdrawn: number;
  available: number;
}

interface WithdrawalRequest {
  id: string;
  event_id?: string | null;
  amount: number;
  status: string;
  requested_at: string;
  expected_payment_date: string | null;
  receipt_url: string | null;
  rejection_reason: string | null;
  bank_name: string | null;
  account_number: string | null;
  events: { title: string } | null;
}

const statusVariants: Record<string, "secondary" | "default" | "destructive"> = {
  requested: "secondary",
  under_review: "secondary",
  processing: "secondary",
  paid: "default",
  rejected: "destructive",
};

async function openReceipt(receiptUrl: string, errorTitle: string) {
  if (/^https?:\/\//.test(receiptUrl)) {
    window.open(receiptUrl, "_blank", "noopener,noreferrer");
    return;
  }
  const { data, error } = await supabase.storage.from("withdrawal-receipts").createSignedUrl(receiptUrl, 120);
  if (error || !data?.signedUrl) {
    toast({ title: errorTitle, variant: "destructive" });
    return;
  }
  window.open(data.signedUrl, "_blank", "noopener,noreferrer");
}

function Metric({
  label,
  value,
  note,
  hero = false,
  href,
  onClick,
  ctaText,
}: {
  label: string;
  value: number;
  note: string;
  hero?: boolean;
  href?: string;
  onClick?: () => void;
  ctaText?: string;
}) {
  const isClickable = Boolean(href || onClick);
  const content = (
    <div
      className={cn(
        panelSurface,
        "group relative min-w-0 p-3.5 sm:p-5 xl:p-6 transition-all duration-200 ease-out",
        hero ? "col-span-2 sm:col-span-2 xl:col-span-1" : "col-span-1",
        isClickable && "cursor-pointer hover:border-border/90 hover:bg-secondary/20 hover:shadow-xs active:scale-[0.98]"
      )}
      onClick={onClick}
    >
      <div className="flex items-center justify-between gap-2">
        <p className={cn(panelEyebrow, "text-[9px] sm:text-[10px] leading-tight")}>{label}</p>
        {isClickable && (
          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-secondary/80 text-muted-foreground transition-all duration-200 group-hover:bg-primary/10 group-hover:text-primary group-hover:scale-110">
            <ArrowUpRight className="h-3 w-3 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </span>
        )}
      </div>
      <p
        className={cn(
          "mt-1.5 break-words font-display font-semibold leading-none tabular-nums tracking-tight sm:mt-2",
          hero ? "text-[clamp(1.5rem,5vw,2rem)]" : "text-[clamp(1.15rem,3.5vw,1.75rem)]",
        )}
      >
        {formatBRL(value)}
      </p>
      <div className="mt-1.5 flex items-center justify-between gap-2 text-[11px] leading-snug sm:text-xs">
        <p className="text-muted-foreground truncate">{note}</p>
        {isClickable && (
          <span className="inline-flex items-center gap-0.5 font-medium text-primary opacity-100 sm:opacity-0 transition-opacity duration-200 group-hover:opacity-100 shrink-0 text-[10px] sm:text-xs">
            <span>{ctaText || "Ver previsão"}</span>
            <ArrowUpRight className="h-3 w-3" />
          </span>
        )}
      </div>
    </div>
  );

  if (href) {
    return (
      <a
        href={href}
        className="block no-underline"
        onClick={(e) => {
          if (onClick) {
            e.preventDefault();
            onClick();
          }
        }}
      >
        {content}
      </a>
    );
  }

  return content;
}

function MethodRow({ icon: Icon, title, value, available, releaseText }: { icon: typeof QrCode; title: string; value: number; available: boolean; releaseText: string }) {
  return (
    <div className={cn(panelRow, "flex flex-wrap items-center gap-3 px-3.5 py-3 sm:flex-nowrap sm:px-4 sm:py-3.5")}>
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary/70 text-muted-foreground"><Icon className="h-4 w-4" /></span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium">{title}</p>
          <Badge variant="outline" className="rounded-full px-2 py-0 text-[10px] font-normal">{available ? "Disponível" : "A liberar"}</Badge>
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">{releaseText}</p>
      </div>
      <p className="ml-12 w-full shrink-0 text-right font-display text-sm font-semibold tabular-nums sm:ml-0 sm:w-auto sm:text-base">{formatBRL(value)}</p>
    </div>
  );
}

function ReconciliationRow({ label, value, subtract = false, strong = false }: { label: string; value: number; subtract?: boolean; strong?: boolean }) {
  return (
    <div className={cn("flex items-start justify-between gap-3 py-2 text-xs sm:items-center sm:gap-4 sm:text-sm", strong && "mt-1 border-t border-border/60 pt-3 font-semibold")}>
      <span className={strong ? "text-foreground" : "text-muted-foreground"}>{label}</span>
      <span className="shrink-0 font-medium tabular-nums">{subtract && value > 0 ? "− " : ""}{formatBRL(value)}</span>
    </div>
  );
}

export default function ProducerWithdrawals({ producerId, organizationId, view = "form" }: { producerId: string; organizationId?: string; view?: "form" | "history" | "forecast" }) {
  const { t } = useTranslation("producerFinancial");
  const queryClient = useQueryClient();
  const isOrg = Boolean(organizationId);
  const ownerKey = isOrg ? `org:${organizationId}` : producerId;
  const [searchParams, setSearchParams] = useSearchParams();
  const [rulesOpen, setRulesOpen] = useState(false);
  const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState("all");
  const [selectedBankId, setSelectedBankId] = useState("");
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const { slot } = useEventFocusSlot();
  // Saques mantém o menu principal e usa o seletor de evento no conteúdo.
  const navSlot = view === "form" ? null : slot;
  const { role: userPlatformRole } = useAuth();
  const isPlatformAdmin = userPlatformRole === "admin";
  const { organizations } = useOrganization();
  const myOrg = organizationId
    ? organizations.find((o) => o.org_id === organizationId && o.member_status === "active")
    : undefined;
  const myOrgRole = myOrg?.org_role as OrganizationRole | undefined;
  const myCustomPerms = (myOrg?.custom_permissions ?? []) as OrgPermission[];

  const canWithdraw = isPlatformAdmin
    || !isOrg
    || (myOrgRole ? ["owner", "admin", "finance"].includes(myOrgRole) : false)
    || myCustomPerms.includes("view_financial");


  const { data: balances = [], isLoading: isLoadingBalances } = useQuery({
    queryKey: ["producer-withdrawal-overview", ownerKey],
    queryFn: async () => {
      const overviewRpc = supabase.rpc.bind(supabase) as unknown as (
        fn: string,
        args: Record<string, string | undefined>,
      ) => Promise<{ data: unknown; error: Error | null }>;
      const { data, error } = isOrg
        ? await overviewRpc("get_organization_withdrawal_overview", { p_org_id: organizationId })
        : await overviewRpc("get_producer_withdrawal_overview", { p_producer_id: producerId });
      if (error) throw error;
      return (data || []) as BalanceRow[];
    },
    staleTime: 30_000,
  });

  const { data: bankAccounts = [] } = useQuery({
    queryKey: ["bank-accounts", ownerKey],
    queryFn: async () => {
      let query = supabase.from("bank_accounts").select("id, account_name, bank_name, agency, account_number, is_default");
      query = isOrg ? query.eq("organization_id", organizationId) : query.eq("producer_id", producerId);
      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
    staleTime: 30_000,
  });

  const { data: withdrawals = [], isLoading: isLoadingWithdrawals } = useQuery({
    queryKey: ["producer-withdrawals", ownerKey],
    queryFn: async () => {
      let query = supabase.from("producer_withdrawals").select("id, event_id, amount, status, requested_at, expected_payment_date, receipt_url, rejection_reason, bank_name, account_number, events(title)").order("requested_at", { ascending: false });
      query = isOrg ? query.eq("organization_id", organizationId) : query.eq("producer_id", producerId);
      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as unknown as WithdrawalRequest[];
    },
    staleTime: 30_000,
  });

  const { data: eventMetaMap = {} } = useQuery({
    queryKey: ["producer-withdrawal-event-meta", ownerKey],
    queryFn: async () => {
      let query = supabase.from("events").select("id, cover_image_url, start_date, end_date");
      query = isOrg ? query.eq("organization_id", organizationId) : query.eq("producer_id", producerId);
      const { data, error } = await query;
      if (error) return {};
      const map: Record<string, { cover_image_url?: string | null; start_date?: string | null; end_date?: string | null }> = {};
      for (const ev of data || []) {
        map[ev.id] = { cover_image_url: ev.cover_image_url, start_date: ev.start_date, end_date: ev.end_date };
      }
      return map;
    },
    staleTime: 60_000,
  });

  const enrichedBalances = useMemo(() => {
    return balances.map((b) => {
      const meta = eventMetaMap[b.event_id];
      const eventEnd = b.event_end || meta?.end_date || meta?.start_date || null;
      return {
        ...b,
        event_end: eventEnd,
        cover_image_url: meta?.cover_image_url ?? null,
        start_date: meta?.start_date ?? null,
      };
    });
  }, [balances, eventMetaMap]);

  useEffect(() => {
    if (!selectedBankId && bankAccounts.length) setSelectedBankId((bankAccounts.find((account) => account.is_default) || bankAccounts[0]).id);
  }, [bankAccounts, selectedBankId]);

  const visibleBalances = useMemo(() => selectedEventId === "all" ? enrichedBalances : enrichedBalances.filter((balance) => balance.event_id === selectedEventId), [enrichedBalances, selectedEventId]);
  const summary = useMemo(() => visibleBalances.reduce((total, balance) => ({
    gross: total.gross + Number(balance.gross_total),
    refunds: total.refunds + Number(balance.refunds_total),
    platformFee: total.platformFee + Number(balance.platform_fee_total),
    gatewayFee: total.gatewayFee + Number(balance.gateway_fee_total),
    pix: total.pix + Number(balance.pix_total),
    pixReleased: total.pixReleased + Number(balance.pix_released),
    pixPending: total.pixPending + Number(balance.pix_pending_release),
    creditCard: total.creditCard + Number(balance.credit_card_total),
    boleto: total.boleto + Number(balance.boleto_total),
    available: total.available + Number(balance.available),
    pending: total.pending + Number(balance.pending_withdrawal),
    withdrawn: total.withdrawn + Number(balance.total_withdrawn),
  }), { gross: 0, refunds: 0, platformFee: 0, gatewayFee: 0, pix: 0, pixReleased: 0, pixPending: 0, creditCard: 0, boleto: 0, available: 0, pending: 0, withdrawn: 0 }), [visibleBalances]);

  const selectedEvent = enrichedBalances.find((balance) => balance.event_id === selectedEventId);
  const selectedBank = bankAccounts.find((account) => account.id === selectedBankId);
  const amount = parseWithdrawalAmount(withdrawAmount);
  const amountTooHigh = Boolean(selectedEvent && amount > Number(selectedEvent.available) + 0.001);
  const expectedPayment = useMemo(() => addBusinessDays(new Date(), 2), []);
  const nextPixRelease = visibleBalances.map((balance) => balance.pix_next_release).filter(Boolean).sort()[0] || null;
  const nextEventRelease = visibleBalances.map((balance) => balance.post_event_release).filter(Boolean).sort()[0] || null;
  const allPostEventReleased = visibleBalances.length > 0 && visibleBalances.every((item) => item.post_event_released);
  const netSales = summary.pix + summary.creditCard + summary.boleto;
  const toRelease = visibleBalances.reduce(
    (total, balance) => total + Number(balance.pix_pending_release || 0) + (balance.post_event_released ? 0 : Number(balance.credit_card_total || 0) + Number(balance.boleto_total || 0)),
    0
  );
  const totalAvailable = useMemo(
    () => balances.reduce((sum, b) => sum + Number(b.available || 0), 0),
    [balances]
  );

  const sidebarPortal = navSlot
    ? createPortal(
        <ProducerFinancialEventSidebar
          balances={enrichedBalances}
          selectedEventId={selectedEventId}
          onSelectEvent={(val) => {
            setSelectedEventId(val);
            setWithdrawAmount("");
          }}
          totalAvailable={totalAvailable}
          activeTab={view === "form" ? "saques" : view === "forecast" ? "previsao" : "historico"}
          onTabChange={(tab) => {
            const next = new URLSearchParams(searchParams);
            next.set("tab", tab);
            setSearchParams(next);
          }}
          onOpenRulesModal={() => setRulesOpen(true)}
        />,
        navSlot
      )
    : null;

  const { data: eventOrders = [] } = useQuery({
    queryKey: ["producer-event-orders-forecast", ownerKey, selectedEventId],
    queryFn: async () => {
      let query = supabase
        .from("orders")
        .select("id, event_id, total, platform_fee, payment_gateway_fee, refunded_amount, payment_method, status, payment_status, paid_at, created_at")
        .eq("status", "paid")
        .eq("payment_status", "paid");
      if (selectedEventId !== "all") {
        query = query.eq("event_id", selectedEventId);
      } else {
        const ids = balances.map((b) => b.event_id);
        if (ids.length === 0) return [];
        query = query.in("event_id", ids);
      }
      const { data, error } = await query;
      if (error) return [];
      return data || [];
    },
    enabled: balances.length > 0,
    staleTime: 30_000,
  });

  const { data: stats } = useQuery({
    queryKey: isOrg
      ? ["organization-dashboard", organizationId, selectedEventId]
      : ["producer-dashboard", producerId, selectedEventId],
    queryFn: () => {
      if (isOrg) {
        return getProducerDashboardStats(producerId, organizationId!, selectedEventId === "all" ? undefined : selectedEventId);
      }
      return getProducerDashboardStats(producerId, undefined, selectedEventId === "all" ? undefined : selectedEventId);
    },
    enabled: !!producerId,
    staleTime: 3 * 60_000,
  });

  const weeklyBlock = useMemo(() => {
    const last = withdrawals.find((withdrawal) => withdrawal.status !== "rejected");
    if (!last) return null;
    const requestedAt = new Date(last.requested_at);
    const unlocksAt = new Date(requestedAt);
    unlocksAt.setDate(unlocksAt.getDate() + 7);
    return unlocksAt > new Date() ? { unlocksAt, requestedAt } : null;
  }, [withdrawals]);

  const requestMutation = useMutation({
    mutationFn: async () => {
      if (!selectedEvent) throw new Error(t("withdrawals.toast.noEvent"));
      if (amount <= 0) throw new Error(t("withdrawals.toast.invalidAmount"));
      if (!selectedBankId) throw new Error(t("withdrawals.toast.noBank"));
      const { data, error } = isOrg
        ? await supabase.rpc("request_organization_withdrawal", { p_org_id: organizationId!, p_event_id: selectedEvent.event_id, p_amount: amount, p_bank_account_id: selectedBankId })
        : await supabase.rpc("request_producer_withdrawal", { p_producer_id: producerId, p_event_id: selectedEvent.event_id, p_amount: amount, p_bank_account_id: selectedBankId });
      if (error) throw error;
      const result = data as unknown as { success?: boolean; error?: string } | null;
      if (result?.success === false) throw new Error(result.error || t("withdrawals.toast.errorGeneric"));
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["producer-withdrawal-overview", ownerKey] });
      queryClient.invalidateQueries({ queryKey: ["producer-withdrawals", ownerKey] });
      setWithdrawAmount("");
      toast({ title: t("withdrawals.toast.requestedTitle"), description: t("withdrawals.toast.requestedDesc") });
    },
    onError: (error: Error) => toast({ title: t("withdrawals.toast.errorTitle"), description: error.message, variant: "destructive" }),
  });

  const columns: DataTableColumn<WithdrawalRequest>[] = [
    {
      key: "requested_at",
      header: t("withdrawals.history.date"),
      render: (row) => <span>{row.requested_at ? format(new Date(row.requested_at), "dd/MM/yyyy") : "—"}</span>,
    },
    {
      key: "event",
      header: t("withdrawals.history.event"),
      render: (row) => <span className="block max-w-[200px] truncate font-medium">{row.events?.title || t("withdrawals.history.general")}</span>,
    },
    {
      key: "bank",
      header: t("withdrawals.history.destinationBank"),
      render: (row) => <span className="text-xs text-muted-foreground">{row.bank_name || "Banco"} ({row.account_number ? `•••• ${row.account_number.slice(-4)}` : "PIX"})</span>,
    },
    {
      key: "amount",
      header: t("withdrawals.history.amount"),
      render: (row) => <span className="font-semibold tabular-nums">{formatBRL(row.amount)}</span>,
    },
    {
      key: "expected_payment_date",
      header: t("withdrawals.history.expectedDate"),
      render: (row) => <span className="text-xs text-muted-foreground">{row.expected_payment_date ? format(new Date(`${row.expected_payment_date}T12:00:00`), "dd/MM/yyyy") : "—"}</span>,
    },
    {
      key: "status",
      header: t("withdrawals.history.status"),
      render: (row) => <Badge variant={statusVariants[row.status] || "secondary"} className="rounded-full font-normal">{row.status ? t(`withdrawals.status.${row.status}`) : "—"}</Badge>,
    },
    {
      key: "receipt",
      header: t("withdrawals.history.receiptTitle"),
      render: (row) => row.receipt_url ? (
        <button
          type="button"
          onClick={() => openReceipt(row.receipt_url!, t("withdrawals.receipt.errorTitle"))}
          className="inline-flex items-center gap-1 text-xs font-medium hover:underline text-primary"
        >
          {t("withdrawals.receipt.view")} <ArrowUpRight className="h-3 w-3" />
        </button>
      ) : (
        <span className="text-xs text-muted-foreground">—</span>
      ),
    },
  ];

  if (view === "history") {
    return (
      <>
        {sidebarPortal}
        <div className={panelSurface}>
        <div className={panelSectionHeader}><h2 className={cn(panelSectionTitle, "flex items-center gap-2")}><span className={panelAccentDot} />{t("withdrawals.history.title")}</h2></div>
        <div className="hidden p-5 md:block"><DataTable columns={columns} data={withdrawals} isLoading={isLoadingWithdrawals} emptyMessage={t("withdrawals.history.empty")} /></div>
        <div className="divide-y divide-border/50 md:hidden">
          {isLoadingWithdrawals ? (
            <p className="p-6 text-center text-xs text-muted-foreground">{t("withdrawals.history.loading")}</p>
          ) : withdrawals.length === 0 ? (
            <p className="p-6 text-center text-xs text-muted-foreground">{t("withdrawals.history.empty")}</p>
          ) : (
            withdrawals.map((request) => (
              <div key={request.id} className="space-y-3 p-4 sm:p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-foreground truncate">
                      {request.events?.title || t("withdrawals.history.general")}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {request.requested_at ? format(new Date(request.requested_at), "dd/MM/yyyy · HH:mm") : "—"}
                    </p>
                  </div>
                  <Badge variant={statusVariants[request.status] || "secondary"} className="rounded-full font-normal shrink-0">
                    {request.status ? t(`withdrawals.status.${request.status}`) : "—"}
                  </Badge>
                </div>

                <div className="flex items-end justify-between gap-3 pt-1">
                  <div className="text-xs text-muted-foreground space-y-0.5">
                    <p className="font-medium text-foreground/80">
                      {request.bank_name || "Banco"} · •••• {request.account_number?.slice(-4) || "PIX"}
                    </p>
                    {request.expected_payment_date && (
                      <p className="text-[11px] text-muted-foreground">
                        Previsão: {format(new Date(`${request.expected_payment_date}T12:00:00`), "dd/MM/yyyy")}
                      </p>
                    )}
                  </div>
                  <p className="font-display text-lg font-bold tabular-nums text-foreground">
                    {formatBRL(request.amount)}
                  </p>
                </div>

                {request.status === "rejected" && request.rejection_reason && (
                  <div className="rounded-xl bg-destructive/10 border border-destructive/20 p-2.5 text-xs text-destructive">
                    <span className="font-semibold">Motivo:</span> {request.rejection_reason}
                  </div>
                )}

                {request.receipt_url && (
                  <button
                    type="button"
                    onClick={() => openReceipt(request.receipt_url!, t("withdrawals.receipt.errorTitle"))}
                    className="flex h-11 w-full items-center justify-center gap-1.5 rounded-xl border border-primary/20 bg-primary/5 text-xs font-semibold text-primary hover:bg-primary/10 active:scale-[0.98] transition-all min-h-[44px]"
                  >
                    <span>{t("withdrawals.receipt.view")}</span>
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
  }

  if (view === "forecast") {
    return (
      <>
        {sidebarPortal}
        <div className="space-y-4 sm:space-y-5">
          <WithdrawalRulesModal open={rulesOpen} onOpenChange={setRulesOpen} />
          
          {/* Seletor de Evento: Quando navSlot está ativo na sidebar, mostra banner sutil do filtro; senão, dropdown fallback */}
          {navSlot ? (
            selectedEvent ? (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border/50 bg-secondary/35 px-4 py-2.5">
                <div className="flex items-center gap-2.5 min-w-0">
                  <EventImage
                    src={selectedEvent.cover_image_url}
                    alt={selectedEvent.event_title}
                    className="h-8 w-8 rounded-xl object-cover border border-border/40 shrink-0"
                  />
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-foreground truncate">
                      Filtro ativo: {selectedEvent.event_title}
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      Exibindo saques, regras de liberação e fluxo de caixa deste evento
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedEventId("all");
                    setWithdrawAmount("");
                  }}
                  className="rounded-full border border-border/60 bg-background/80 hover:bg-secondary px-3 py-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-all flex items-center gap-1.5 shrink-0 active:scale-[0.98]"
                >
                  <span>Visão consolidada</span>
                  <X className="h-3 w-3 opacity-70" />
                </button>
              </div>
            ) : null
          ) : (
            <div className="flex flex-col items-start gap-2 w-full">
              <div className="flex items-center justify-between gap-2.5 w-full">
                <span className={cn(panelEyebrow, "text-[10px]")}>Filtrar por evento</span>
                {selectedEventId !== "all" && (
                  <button
                    type="button"
                    onClick={() => setSelectedEventId("all")}
                    className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-secondary/60 px-3 py-1 text-xs font-medium text-muted-foreground hover:bg-secondary hover:text-foreground active:scale-[0.97] transition-all"
                  >
                    <span>Ver todos</span>
                    <X className="h-3 w-3 opacity-70" />
                  </button>
                )}
              </div>
              <WithdrawalEventPicker
                value={selectedEventId}
                onValueChange={setSelectedEventId}
                events={enrichedBalances}
                totalAvailable={totalAvailable}
              />
            </div>
          )}

        {/* Métricas */}
        <div className="grid grid-cols-2 gap-2.5 sm:gap-3 xl:grid-cols-4">
          <Metric label="Disponível hoje" value={summary.available} note={selectedEvent ? `Saldo de ${selectedEvent.event_title}` : isOrg ? "Total da organização" : "Total da conta"} hero />
          <Metric label="A liberar no prazo" value={toRelease} note="Valores dentro do prazo regulamentar" />
          <Metric label="Projeção pós-evento" value={Math.max(summary.available, netSales - summary.withdrawn)} note="Total líquido final previsto" />
          <Metric label="Já recebido" value={summary.withdrawn} note="Saques concluídos" />
        </div>

        {/* Gráfico Forecast Apple Minimalista */}
        <section className={panelSurface}>
          <div className={panelSectionHeader}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className={cn(panelSectionTitle, "flex items-center gap-2")}>
                  <span className={panelAccentDot} />
                  Linha do tempo de liberação & fluxo de caixa
                </h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  {selectedEvent
                    ? `Projeção de liberação de ${selectedEvent.event_title}. PIX em D+2 e Cartão/Boleto liberados 1 dia após o término.`
                    : "Visão consolidada das liberações de todos os eventos. Cada evento destrava seus cartões em sua respectiva data."}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setRulesOpen(true)}
                className="rounded-full border border-border/60 bg-secondary/50 px-3.5 py-1.5 text-xs font-medium text-muted-foreground hover:bg-secondary hover:text-foreground transition-all shrink-0"
              >
                Entenda os prazos
              </button>
            </div>
          </div>
          <div className="p-4 sm:p-5">
            <ProducerWithdrawalGantt
              summary={summary}
              netSales={netSales}
              toRelease={toRelease}
              selectedEvent={selectedEvent}
              nextPixRelease={nextPixRelease}
              nextEventRelease={nextEventRelease}
              allPostEventReleased={allPostEventReleased}
              balances={enrichedBalances}
              orders={eventOrders}
              timelineByDay={stats?.timelineByDay || []}
              withdrawals={withdrawals}
              onOpenRulesModal={() => setRulesOpen(true)}
            />
          </div>
        </section>

        {/* Tabela Destrinchada: Liberação por Evento */}
        <section className={panelSurface}>
          <div className={panelSectionHeader}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h2 className={cn(panelSectionTitle, "flex items-center gap-2")}>
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/60" />
                  Detalhamento de liberação por evento ({enrichedBalances.length})
                </h2>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Acompanhe as datas de término e o destravamento de cartão e boleto de cada evento.
                </p>
              </div>
              {selectedEventId !== "all" && (
                <button
                  type="button"
                  onClick={() => setSelectedEventId("all")}
                  className="rounded-full border border-border/60 bg-secondary/50 px-2.5 py-1 text-[11px] font-medium text-muted-foreground hover:bg-secondary hover:text-foreground transition-all flex items-center gap-1"
                >
                  <span>Ver todos os eventos</span>
                  <X className="h-2.5 w-2.5 opacity-70" />
                </button>
              )}
            </div>
          </div>

          {/* Visualização Mobile: Cards táteis sem necessidade de rolagem horizontal de 740px */}
          <div className="divide-y divide-border/40 p-2 sm:p-3 md:hidden">
            {enrichedBalances.map((b) => {
              const bPixTotal = Number(b.pix_released || 0) + Number(b.pix_pending_release || 0);
              const bCardBoleto = Number(b.credit_card_total || 0) + Number(b.boleto_total || 0);
              const isSelected = selectedEventId === b.event_id;
              return (
                <div
                  key={`card-${b.event_id}`}
                  onClick={() => setSelectedEventId(isSelected ? "all" : b.event_id)}
                  className={cn(
                    "p-3 rounded-2xl transition-all cursor-pointer space-y-2.5 active:scale-[0.99]",
                    isSelected
                      ? "bg-secondary/70 dark:bg-secondary/40 border border-border/70 shadow-2xs"
                      : "border border-transparent hover:bg-secondary/35"
                  )}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <EventImage
                      src={b.cover_image_url}
                      alt={b.event_title}
                      className="h-10 w-10 rounded-xl object-cover border border-border/40 shrink-0"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-semibold text-xs sm:text-sm text-foreground truncate">{b.event_title}</p>
                        {isSelected && (
                          <span className="inline-flex items-center rounded-full bg-foreground/10 px-2 py-0.5 text-[10px] font-medium text-foreground shrink-0">
                            Filtrado
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-muted-foreground truncate mt-0.5">
                        {b.event_end
                          ? `Término em ${format(new Date(b.event_end), "dd/MM/yyyy HH:mm")}`
                          : "Data de término não definida"}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border/30 text-xs">
                    <div className="space-y-0.5">
                      <span className="text-[10px] font-medium uppercase text-muted-foreground">Disponível</span>
                      <p className={cn(
                        "font-display font-semibold tabular-nums",
                        Number(b.available || 0) > 0
                          ? "text-emerald-600 dark:text-emerald-400"
                          : "text-muted-foreground font-normal"
                      )}>
                        {formatBRL(b.available)}
                      </p>
                    </div>
                    <div className="space-y-0.5 text-right">
                      <span className="text-[10px] font-medium uppercase text-muted-foreground">PIX (D+2)</span>
                      <p className="font-display font-medium tabular-nums text-foreground">
                        {formatBRL(bPixTotal)}
                      </p>
                    </div>
                    <div className="space-y-0.5 pt-1.5 border-t border-border/20">
                      <span className="text-[10px] font-medium uppercase text-muted-foreground">Cartão / Boleto</span>
                      <p className="font-display font-medium tabular-nums text-foreground">
                        {formatBRL(bCardBoleto)}
                      </p>
                    </div>
                    <div className="space-y-0.5 pt-1.5 border-t border-border/20 text-right">
                      <span className="text-[10px] font-medium uppercase text-muted-foreground">Pós-evento</span>
                      <div>
                        {b.post_event_released ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 text-[10px] font-medium text-emerald-600 dark:text-emerald-400">
                            Liberado
                          </span>
                        ) : b.post_event_release ? (
                          <span className="font-display font-medium text-foreground">
                            {format(new Date(b.post_event_release), "dd/MM/yyyy")}
                          </span>
                        ) : (
                          <span className="text-[11px] text-muted-foreground">Pós-evento</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Visualização Desktop: Tabela completa (inalterada) */}
          <div className="hidden md:block overflow-x-auto p-3 sm:p-4">
            {/* Cabeçalho da Tabela Apple Minimalista */}
            <div className="grid grid-cols-[1fr_110px_110px_130px_130px] items-center gap-4 px-3 py-2 text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground border-b border-border/40 min-w-[740px]">
              <div>Evento</div>
              <div className="text-right">Disponível</div>
              <div className="text-right">PIX (D+2)</div>
              <div className="text-right">Cartão / Boleto</div>
              <div className="text-right">Liberação pós-evento</div>
            </div>

            {/* Linhas da Tabela com Alinhamento Perfeito */}
            <div className="divide-y divide-border/25 min-w-[740px] mt-1">
              {enrichedBalances.map((b) => {
                const bPixTotal = Number(b.pix_released || 0) + Number(b.pix_pending_release || 0);
                const bCardBoleto = Number(b.credit_card_total || 0) + Number(b.boleto_total || 0);
                const isSelected = selectedEventId === b.event_id;
                return (
                  <div
                    key={b.event_id}
                    onClick={() => setSelectedEventId(isSelected ? "all" : b.event_id)}
                    className={cn(
                      "grid grid-cols-[1fr_110px_110px_130px_130px] items-center gap-4 py-2.5 px-3 rounded-xl transition-all cursor-pointer text-xs",
                      isSelected
                        ? "bg-secondary/70 dark:bg-secondary/40 border border-border/70 text-foreground shadow-2xs"
                        : "border border-transparent hover:bg-secondary/35 text-foreground/90"
                    )}
                  >
                    {/* Coluna 1: Evento */}
                    <div className="flex items-center gap-3 min-w-0">
                      <EventImage
                        src={b.cover_image_url}
                        alt={b.event_title}
                        className="h-9 w-9 rounded-xl object-cover border border-border/40 shrink-0"
                      />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-xs text-foreground truncate">{b.event_title}</p>
                          {isSelected && (
                            <span className="inline-flex items-center rounded-full bg-foreground/10 px-2 py-0.5 text-[10px] font-medium text-foreground shrink-0">
                              Filtrado
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-muted-foreground truncate">
                          {b.event_end
                            ? `Término em ${format(new Date(b.event_end), "dd/MM/yyyy HH:mm")}`
                            : "Data de término não definida"}
                        </p>
                      </div>
                    </div>

                    {/* Coluna 2: Disponível */}
                    <div className="text-right">
                      <span
                        className={cn(
                          "font-display tabular-nums font-semibold",
                          Number(b.available || 0) > 0
                            ? "text-emerald-600 dark:text-emerald-400"
                            : "text-muted-foreground font-normal"
                        )}
                      >
                        {formatBRL(b.available)}
                      </span>
                    </div>

                    {/* Coluna 3: PIX (D+2) */}
                    <div className="text-right">
                      <span className="font-display tabular-nums font-medium text-foreground">
                        {formatBRL(bPixTotal)}
                      </span>
                    </div>

                    {/* Coluna 4: Cartão/Boleto retido */}
                    <div className="text-right">
                      <span className="font-display tabular-nums font-medium text-foreground">
                        {formatBRL(bCardBoleto)}
                      </span>
                    </div>

                    {/* Coluna 5: Liberação pós-evento */}
                    <div className="text-right">
                      {b.post_event_released ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 text-[10px] font-medium text-emerald-600 dark:text-emerald-400">
                          Liberado
                        </span>
                      ) : b.post_event_release ? (
                        <span className="font-display tabular-nums font-medium text-foreground">
                          {format(new Date(b.post_event_release), "dd/MM/yyyy")}
                        </span>
                      ) : (
                        <span className="text-[11px] text-muted-foreground">Pós-evento</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      </div>
    </>
  );
  }

  const canSubmit = Boolean(selectedEvent && selectedBank && amount > 0 && !amountTooHigh && !weeklyBlock && !requestMutation.isPending && canWithdraw);

  return (
    <>
      {sidebarPortal}
      <div className="space-y-4 sm:space-y-5">
        <WithdrawalRulesModal open={rulesOpen} onOpenChange={setRulesOpen} />
        <WithdrawalRequestModal
          open={withdrawModalOpen}
          onOpenChange={setWithdrawModalOpen}
          balances={balances}
          bankAccounts={bankAccounts}
          preselectedEventId={selectedEventId}
          canWithdraw={canWithdraw}
          onSubmit={async ({ eventId, amount: modalAmount, bankAccountId }) => {
            const { error } = isOrg
              ? await supabase.rpc("request_organization_withdrawal", {
                  p_org_id: organizationId!,
                  p_event_id: eventId,
                  p_amount: modalAmount,
                  p_bank_account_id: bankAccountId,
                })
              : await supabase.rpc("request_producer_withdrawal", {
                  p_producer_id: producerId,
                  p_event_id: eventId,
                  p_amount: modalAmount,
                  p_bank_account_id: bankAccountId,
                });
            if (error) throw error;
            queryClient.invalidateQueries({ queryKey: ["producer-withdrawal-overview", ownerKey] });
            queryClient.invalidateQueries({ queryKey: ["producer-withdrawals", ownerKey] });
            toast({ title: t("withdrawals.toast.requestedTitle"), description: t("withdrawals.toast.requestedDesc") });
          }}
          isSubmitting={requestMutation.isPending}
          weeklyBlock={weeklyBlock}
        />
        {/* Seletor de Evento: Quando navSlot está ativo na sidebar, mostra banner sutil do filtro se houver evento selecionado; senão, dropdown fallback */}
        {navSlot ? (
          selectedEvent ? (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border/50 bg-secondary/35 px-4 py-2.5">
              <div className="flex items-center gap-2.5 min-w-0">
                <EventImage
                  src={selectedEvent.cover_image_url}
                  alt={selectedEvent.event_title}
                  className="h-8 w-8 rounded-xl object-cover border border-border/40 shrink-0"
                />
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-foreground truncate">
                    Filtro ativo: {selectedEvent.event_title}
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    Exibindo saques e saldo disponível deste evento
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSelectedEventId("all");
                  setWithdrawAmount("");
                }}
                className="rounded-full border border-border/60 bg-background/80 hover:bg-secondary px-3 py-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-all flex items-center gap-1.5 shrink-0 active:scale-[0.98]"
              >
                <span>Visão consolidada</span>
                <X className="h-3 w-3 opacity-70" />
              </button>
            </div>
          ) : null
        ) : (
          <div className="flex flex-col items-start gap-2 w-full">
            <div className="flex items-center justify-between gap-2.5 w-full">
              <span className={cn(panelEyebrow, "text-[10px]")}>Filtrar por evento</span>
              {selectedEventId !== "all" && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedEventId("all");
                    setWithdrawAmount("");
                  }}
                  className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-secondary/60 px-3 py-1 text-xs font-medium text-muted-foreground hover:bg-secondary hover:text-foreground active:scale-[0.97] transition-all"
                >
                  <span>Ver todos</span>
                  <X className="h-3 w-3 opacity-70" />
                </button>
              )}
            </div>
            <WithdrawalEventPicker
              value={selectedEventId}
              onValueChange={(value) => {
                setSelectedEventId(value);
                setWithdrawAmount("");
              }}
              events={enrichedBalances}
              totalAvailable={totalAvailable}
            />
          </div>
        )}

      <div className="grid grid-cols-2 gap-2.5 sm:gap-3 xl:grid-cols-4">
        <Metric label="Disponível para saque" value={summary.available} note={selectedEvent ? `Saldo de ${selectedEvent.event_title}` : isOrg ? "Total da organização" : "Total da conta"} hero />
        <Metric
          label="A liberar"
          value={toRelease}
          note="Valores confirmados dentro do prazo"
          href="?tab=previsao"
          onClick={() => {
            const next = new URLSearchParams(searchParams);
            next.set("tab", "previsao");
            setSearchParams(next);
          }}
          ctaText="Ver previsão"
        />
        <Metric label="Em análise" value={summary.pending} note="Saques já solicitados" />
        <Metric label="Já recebido" value={summary.withdrawn} note="Saques concluídos" />
      </div>

      {isLoadingBalances ? <div className="h-72 animate-pulse rounded-[28px] bg-secondary/50" /> : balances.length === 0 ? (
        <div className={cn(panelEmpty, "p-10 text-center")}><WalletCards className="mx-auto h-5 w-5 text-muted-foreground" /><p className="mt-3 text-sm font-medium">Ainda não há vendas pagas para exibir.</p></div>
      ) : (
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px] xl:items-start">
          {/* No mobile a ação de saque fica em primeiro lugar (order-1) e no desktop fica na coluna direita (xl:order-2) */}
          <aside className={cn(panelSurface, "order-1 xl:order-2 xl:sticky xl:top-4")}>
            <div className={panelSectionHeader}>
              <h2 className={cn(panelSectionTitle, "flex items-center gap-2")}>
                <span className={panelAccentDot} />
                Solicitar saque
              </h2>
              <p className="mt-1 text-xs text-muted-foreground">Transferência rápida para sua conta bancária.</p>
            </div>
            <div className="space-y-5 p-4 sm:space-y-6 sm:p-6">
              {!selectedEvent ? (
                <div className="space-y-4">
                  <div className="rounded-[22px] bg-secondary/50 p-4">
                    <p className="text-xs text-muted-foreground">Total liberado para saque</p>
                    <p className="mt-1 font-display text-2xl font-bold tabular-nums text-foreground">
                      {formatBRL(summary.available)}
                    </p>
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      {balances.filter((b) => Number(b.available) > 0).length} {balances.filter((b) => Number(b.available) > 0).length === 1 ? "evento com saldo liberado" : "eventos com saldo liberado"}
                    </p>
                  </div>

                  <div className="space-y-2 rounded-2xl bg-secondary/30 p-3.5 text-xs text-muted-foreground">
                    <div className="flex items-center gap-2 text-foreground font-medium">
                      <Sparkles className="h-3.5 w-3.5 text-foreground" />
                      <span>Visão consolidada</span>
                    </div>
                    <p className="leading-relaxed">
                      Clique no botão abaixo para escolher o evento com saldo liberado e transferir para sua conta bancária.
                    </p>
                  </div>

                  {weeklyBlock && (
                    <div className="flex gap-2 rounded-[22px] bg-amber-500/10 p-3.5 text-amber-800 dark:text-amber-300">
                      <CalendarClock className="mt-0.5 h-4 w-4 shrink-0" />
                      <p className="text-xs leading-relaxed">
                        Próximo saque disponível em{" "}
                        <span className="font-semibold">{format(weeklyBlock.unlocksAt, "dd/MM/yyyy")}</span>.
                      </p>
                    </div>
                  )}

                  {!canWithdraw && (
                    <div className="flex flex-col gap-2.5 rounded-[22px] bg-muted/60 p-3.5 text-muted-foreground text-xs leading-relaxed border border-border/40">
                      <div className="flex gap-2">
                        <Lock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                        <span>
                          Solicitação de saques restrita aos cargos <strong>Proprietário</strong>, <strong>Administrador</strong> ou <strong>Financeiro</strong>.
                        </span>
                      </div>
                      {isOrg && organizationId && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setShowRoleModal(true)}
                          className="mt-1 h-7 text-xs border-primary/30 text-primary hover:bg-primary/10 w-fit self-start gap-1.5 rounded-full"
                        >
                          <KeyRound className="h-3 w-3" />
                          Solicitar acesso aos administradores
                        </Button>
                      )}
                    </div>
                  )}

                  <Button
                    className={cn(
                      panelPill,
                      "h-12 w-full font-semibold text-sm transition-all flex items-center justify-center gap-2 active:scale-[0.98]",
                      canWithdraw
                        ? "bg-primary text-primary-foreground shadow-sm hover:shadow hover:bg-primary/90"
                        : "bg-muted text-muted-foreground opacity-60 cursor-not-allowed hover:bg-muted"
                    )}
                    disabled={!canWithdraw}
                    title={!canWithdraw ? "Ação restrita: apenas os cargos Proprietário, Administrador ou Financeiro podem solicitar saques nesta organização." : undefined}
                    onClick={() => canWithdraw && setWithdrawModalOpen(true)}
                  >
                    {!canWithdraw ? <Lock className="h-4 w-4" /> : <Banknote className="h-4 w-4" />}
                    Solicitar saque
                  </Button>
                </div>
              ) : (
                <>
                  <div className="rounded-2xl border border-border/70 bg-secondary/30 p-3 flex items-center gap-3">
                    <div className="h-12 w-12 rounded-xl overflow-hidden shrink-0 border border-border/60 bg-muted">
                      <EventImage
                        src={selectedEvent.cover_image_url}
                        alt={selectedEvent.event_title}
                        className="h-full w-full object-cover"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold truncate text-foreground" title={selectedEvent.event_title}>
                          {selectedEvent.event_title}
                        </p>
                        <button
                          type="button"
                          onClick={() => setSelectedEventId("all")}
                          className="rounded-full border border-border/60 bg-secondary/60 hover:bg-secondary px-2.5 py-1 text-xs font-medium text-foreground hover:text-foreground shrink-0 active:scale-[0.97] transition-all"
                        >
                          Trocar
                        </button>
                      </div>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        Disponível: <span className="font-semibold text-emerald-600 dark:text-emerald-400">{formatBRL(Number(selectedEvent.available))}</span>
                      </p>
                    </div>
                  </div>
                  {weeklyBlock && (
                    <div className="flex gap-2 rounded-[22px] bg-amber-500/10 p-3.5 text-amber-800 dark:text-amber-300">
                      <CalendarClock className="mt-0.5 h-4 w-4 shrink-0" />
                      <p className="text-xs leading-relaxed">
                        Próximo saque disponível em{" "}
                        <span className="font-semibold">{format(weeklyBlock.unlocksAt, "dd/MM/yyyy")}</span>.
                      </p>
                    </div>
                  )}
                  <div>
                    <label className={panelFieldLabel} htmlFor="withdrawal-amount">Quanto você quer sacar?</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 z-10 -translate-y-1/2 text-sm text-muted-foreground font-semibold">R$</span>
                      <Input
                        id="withdrawal-amount"
                        inputMode="decimal"
                        value={withdrawAmount}
                        onChange={(event) => setWithdrawAmount(event.target.value.replace(/[^\d.,]/g, ""))}
                        onBlur={() => amount > 0 && setWithdrawAmount(formatWithdrawalAmount(amount))}
                        placeholder="0,00"
                        className={cn(panelControl, "pl-11 text-base font-semibold tabular-nums sm:text-lg")}
                      />
                    </div>
                    {amountTooHigh && <p className="mt-1.5 text-xs text-destructive">O valor supera o saldo disponível.</p>}
                    <div className="mt-2.5 grid grid-cols-4 gap-1.5 sm:gap-2">
                      {[25, 50, 75, 100].map((percentage) => (
                        <button
                          key={percentage}
                          type="button"
                          onClick={() => setWithdrawAmount(formatWithdrawalAmount(Number(selectedEvent.available) * percentage / 100))}
                          className="h-11 sm:h-9 rounded-full bg-secondary/60 text-xs sm:text-xs font-semibold text-foreground/80 hover:bg-secondary hover:text-foreground active:scale-[0.96] transition-all min-h-[44px] sm:min-h-0 flex items-center justify-center"
                        >
                          {percentage === 100 ? "Tudo" : `${percentage}%`}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className={panelFieldLabel}>Conta de destino</label>
                    {bankAccounts.length ? (
                      <Select value={selectedBankId} onValueChange={setSelectedBankId}>
                        <SelectTrigger className={panelControl}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {bankAccounts.map((account) => (
                            <SelectItem key={account.id} value={account.id}>
                              {account.account_name} · {account.bank_name} •••• {String(account.account_number || "").slice(-4)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="mt-2 rounded-[22px] border border-dashed border-border/70 p-4 text-center text-xs text-muted-foreground">
                        <Landmark className="mx-auto mb-2 h-4 w-4" />
                        Cadastre uma conta bancária para continuar.
                      </div>
                    )}
                  </div>
                  <div className="rounded-[22px] bg-secondary/50 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-xs text-muted-foreground">Você recebe</span>
                      <span className="font-display text-base font-semibold tabular-nums sm:text-lg">{formatBRL(amount)}</span>
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-3 border-t border-border/50 pt-2">
                      <span className="text-xs text-muted-foreground">Previsão na conta</span>
                      <span className="text-xs font-medium">até {format(expectedPayment, "dd/MM/yyyy")}</span>
                    </div>
                  </div>
                  <Button
                    className={cn(
                      panelPill,
                      "h-12 w-full font-semibold transition-all flex items-center justify-center gap-2 active:scale-[0.98]",
                      canWithdraw
                        ? "bg-primary text-primary-foreground shadow-sm hover:shadow hover:bg-primary/90"
                        : "bg-muted text-muted-foreground opacity-60 cursor-not-allowed hover:bg-muted"
                    )}
                    disabled={!canSubmit || !canWithdraw}
                    title={!canWithdraw ? "Ação restrita: apenas os cargos Proprietário, Administrador ou Financeiro podem solicitar saques nesta organização." : undefined}
                    onClick={() => canWithdraw && requestMutation.mutate()}
                  >
                    {!canWithdraw ? <Lock className="mr-2 h-4 w-4" /> : requestMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Banknote className="mr-2 h-4 w-4" />}
                    Solicitar {amount > 0 ? formatBRL(amount) : "saque"}
                  </Button>

                  {!canWithdraw && isOrg && organizationId && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowRoleModal(true)}
                      className="w-full text-xs border-primary/30 text-primary hover:bg-primary/10 gap-1.5 rounded-full"
                    >
                      <KeyRound className="h-3.5 w-3.5" />
                      Solicitar acesso aos administradores
                    </Button>
                  )}
                </>
              )}
              <div className="flex items-start gap-2 border-t border-border/50 pt-4">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <p className="text-[11px] leading-relaxed text-muted-foreground">
                  Não há outra taxa no saque. Os descontos exibidos são das vendas.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setRulesOpen(true)}
                className="mt-1 flex w-full items-center justify-between rounded-2xl border border-border/70 bg-secondary/30 p-3 text-left transition-all hover:bg-secondary/60 hover:border-border group min-h-[52px]"
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary transition-transform group-hover:scale-105">
                    <Play className="ml-0.5 h-3.5 w-3.5 fill-current" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-foreground">Como funciona a liberação?</p>
                    <p className="truncate text-[11px] text-muted-foreground">Ver animação com regras e prazos</p>
                  </div>
                </div>
                <ArrowUpRight className="h-4 w-4 shrink-0 text-muted-foreground group-hover:text-foreground" />
              </button>
            </div>
          </aside>

          {/* No mobile a reconciliação fica abaixo da ação principal (order-2) e no desktop na coluna esquerda (xl:order-1) */}
          <div className="order-2 xl:order-1 space-y-4">
            <div className="rounded-2xl border border-border/40 bg-secondary/15 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <p className="text-xs font-semibold text-foreground">Previsão e cronograma de liberação</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Acompanhe a evolução do fluxo de caixa e os prazos de PIX (D+2) e cartões na aba de previsão.
                </p>
              </div>
              <a
                href="?tab=previsao"
                className="rounded-full border border-border/60 bg-card px-3.5 py-2 text-xs font-medium text-foreground hover:bg-secondary transition-colors shrink-0 inline-flex items-center gap-1.5 self-start sm:self-center shadow-2xs min-h-[40px]"
              >
                Acessar previsão <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
            </div>
            <section className={panelSurface}>
              <div className={panelSectionHeader}><h2 className={cn(panelSectionTitle, "flex items-center gap-2")}><span className={panelAccentDot} />Como o líquido foi calculado</h2></div>
              <div className="p-4 sm:p-6">
                <ReconciliationRow label="Vendas pagas (bruto)" value={summary.gross} />
                <ReconciliationRow label="Taxa da plataforma" value={summary.platformFee} subtract />
                <ReconciliationRow label="Taxa de processamento do pagamento" value={summary.gatewayFee} subtract />
                <ReconciliationRow label="Estornos e reembolsos" value={summary.refunds} subtract />
                <ReconciliationRow label="Seu valor líquido" value={netSales} strong />
                <div className="mt-4 grid grid-cols-1 gap-2 rounded-[22px] bg-secondary/50 p-3 min-[420px]:grid-cols-3">
                  {[{ label: "PIX", value: summary.pix }, { label: "Cartão", value: summary.creditCard }, { label: "Boleto", value: summary.boleto }].map((item) => <div key={item.label} className="flex min-w-0 items-center justify-between gap-3 min-[420px]:block"><p className="text-[10px] text-muted-foreground">{item.label}</p><p className="mt-0 text-xs font-semibold tabular-nums min-[420px]:mt-1 sm:text-sm">{formatBRL(item.value)}</p></div>)}
                </div>
              </div>
            </section>
          </div>
        </div>
      )}
      </div>

      {isOrg && organizationId && (
        <RequestRoleModal
          open={showRoleModal}
          onOpenChange={setShowRoleModal}
          organizationId={organizationId}
          organizationName={myOrg?.org_name || "Organização"}
          currentRole={myOrgRole}
          initialRole="finance"
        />
      )}
    </>
  );
}
